import React, { useEffect, useState } from "react";
import { usePermissionTabs } from "../../../hooks/usePermissionTabs";
import { PermissionTabManager } from "../../../components/PermissionTabManager";
import FleetVehiclesManagement from "./FleetVehiclesManagement";
import VehicleViewModal from "./VehicleViewModal";
import AddVehiclePane from "./AddVehiclePane";
import { useSelector } from "react-redux";

const ALL_TABS = [
  {
    key: "Vehicles",
    label: "Vehicles",
    component: <FleetVehiclesManagement />,
    modules: ["vehicles"],
    operation: "read",
  },
];

interface FleetProps {
  setHeaderActions?: (node: React.ReactNode) => void;
  setHeaderButtons?: (node: React.ReactNode) => void;
}

const Fleet: React.FC<FleetProps> = ({ setHeaderActions, setHeaderButtons }) => {
  const result = usePermissionTabs({
    tabs: ALL_TABS,
    param: "sub",
    parentTab: "Vehicles",
  });

  const [viewVehicleModal, setViewVehicleModal] = useState(false);
  const [addVehicleModal, setAddVehicleModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);

  const data = useSelector((state: any) => state?.fleet?.data);

  // Keep selectedVehicle in sync with updated vehicles data list
  useEffect(() => {
    if (data?.vehicles && selectedVehicle) {
      const updated = data.vehicles.find((v: any) => v.id === selectedVehicle.id);
      if (updated) {
        setSelectedVehicle(updated);
      }
    }
  }, [data, selectedVehicle?.id]);

  useEffect(() => {
    const handleViewVehicle = (e: any) => {
      setSelectedVehicle(e.detail.vehicle);
      setViewVehicleModal(true);
      setAddVehicleModal(false);
    };

    const handleAddVehicle = () => {
      setSelectedVehicle(null);
      setAddVehicleModal(true);
      setViewVehicleModal(false);
    };

    window.addEventListener("openVehicleModal", handleViewVehicle as EventListener);
    window.addEventListener("openAddVehicleModal", handleAddVehicle as EventListener);

    return () => {
      window.removeEventListener("openVehicleModal", handleViewVehicle as EventListener);
      window.removeEventListener("openAddVehicleModal", handleAddVehicle as EventListener);
    };
  }, []);

  const isModalOpen = viewVehicleModal || addVehicleModal;

  // Clone content with side-pane props so child can react to drawer state
  const clonedContent =
    result.content && React.isValidElement(result.content)
      ? React.cloneElement(result.content as React.ReactElement, {
          isPaneOpen: isModalOpen,
          setHeaderActions,
          setHeaderButtons,
        } as any)
      : result.content;

  // Backdrop overlay (mobile/tablet only)
  const rightOverlay = isModalOpen ? (
    <div
      className="fixed inset-0 bg-black/40 z-20 lg:hidden"
      onClick={() => {
        setViewVehicleModal(false);
        setAddVehicleModal(false);
        setSelectedVehicle(null);
      }}
    />
  ) : null;

  // Side pane / drawer — fixed on mobile, inline col-span-4 on desktop
  const rightPanel = (
    <div className="col-span-12 xl:col-span-5 space-y-4  mt-2 xl:static fixed inset-y-0 right-0 z-[35] w-full sm:w-[450px] xl:w-auto p-4 pt-[76px] sm:pt-[96px] xl:p-0 shadow-2xl xl:shadow-none bg-white xl:bg-transparent overflow-y-auto xl:overflow-visible flex flex-col justify-start">
      {viewVehicleModal && selectedVehicle ? (
        <VehicleViewModal
          vehicle={selectedVehicle}
          open={viewVehicleModal}
          onClose={() => {
            setViewVehicleModal(false);
            setSelectedVehicle(null);
          }}
        />
      ) : addVehicleModal ? (
        <AddVehiclePane
          open={addVehicleModal}
          onClose={() => setAddVehicleModal(false)}
        />
      ) : null}
    </div>
  );

  return (
    <PermissionTabManager
      visibleTabs={result.visibleTabs}
      activeLabel={result.activeLabel ?? ""}
      content={<div className="space-y-4">{clonedContent}</div>}
      isLoading={result.isLoading}
      error={result.error}
      handleChange={result.handleChange} 
      className="relative bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-ful rounded-r-3xl rounded-bl-3xl w-full"
      subClassName=""
      contentClassName="px-4 pb-4 pt-2 sm:px-6 sm:pb-6 sm:pt-2"  
      hideToggle 
      rightPanel={rightPanel}
      showRightPanel={isModalOpen}
      rightOverlay={rightOverlay}
    />
  );
};

export default Fleet;
